import { ButtonGroup } from "@material-ui/core";
import { FC, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { Button } from "../../../../components/button";
import { Flex } from "../../../../components/layout";
import { Loader } from "../../../../components/loader";
import { BillingDTO } from "../../../../store/billing/models/dto";
import { Billing } from "../../../../store/billing/reducer";
import { useFetchable } from "../../../../store/common/fetchable";
import { UsersDTO } from "../../../../store/users/models/dto";
import { Users } from "../../../../store/users/reducer";
import { useBrowserWallet } from "../../../../store/wallets/crypto";
import { toDecimal } from "../../../../utils/utils";
import { Manual } from "./manual";
import { Metamask } from "./metamask";

type FormProps = {
  chain: BillingDTO.Responses.GetSKRTAddresses.T[number];
  amount: number;
};

const Container = styled(Flex)`
  height: 100%;
  min-height: 30em;
  flex-direction: column;
  align-items: flex-start;
`;

const Amount = styled.h3`
  font-size: 2em;
  margin: 0;
`;

const Summary = styled(Flex)`
  flex-direction: column;
  flex-grow: 1;
  width: 100%;
  justify-content: space-between;
`;

const FootNote = styled.div`
  font-size: 0.5em;
`;

const Wrapper = styled(Flex)`
  flex-grow: 1;
  flex-direction: column;
  justify-content: space-around;
  width: 100%;
`;

const StyledButtonGroup = styled(ButtonGroup)`
  margin: 1em 0;
`;

export const Checkout: FC<FormProps> = (props) => {
  const forexRate = useFetchable(Billing.forexRate);
  const currentUser = useFetchable(Users.current);
  return (
    <Loader<
      BillingDTO.Responses.GetForexRate.T | UsersDTO.Responses.GetSingle.T
    >
      data={[forexRate, currentUser]}
    >
      {([loadedRate, loadedUser]) => (
        <InnerCheckout
          {...props}
          rate={(loadedRate as BillingDTO.Responses.GetForexRate.T).rate}
          kycStatus={(loadedUser as UsersDTO.Responses.GetSingle.T).kycStatus}
        />
      )}
    </Loader>
  );
};

type Option = "METAMASK" | "MANUAL";

export const InnerCheckout: FC<
  FormProps &
    BillingDTO.Responses.GetForexRate.T &
    Pick<UsersDTO.Responses.GetSingle.T, "kycStatus">
> = (props) => {
  const { amount, rate } = props;
  const { t } = useTranslation("billing");
  const { isEthAvailable } = useBrowserWallet();
  const [option, setOption] = useState<Option>(
    isEthAvailable ? "METAMASK" : "MANUAL"
  );

  const Component = useMemo(() => {
    switch (option) {
      case "METAMASK":
        return <Metamask {...props} />;
      default:
        return <Manual {...props} />;
    }
  }, [option, props]);

  return (
    <Container>
      <Summary>
        <Amount>USDT {toDecimal(amount * rate)}</Amount>
        {isEthAvailable ? (
          <StyledButtonGroup
            color="primary"
            aria-label="outlined primary button group"
          >
            <Button
              variant={option === "METAMASK" ? "contained" : "outlined"}
              onClick={() => setOption("METAMASK")}
            >
              Metamask
            </Button>
            <Button
              variant={option === "MANUAL" ? "contained" : "outlined"}
              onClick={() => setOption("MANUAL")}
            >
              {t("manually")}
            </Button>
          </StyledButtonGroup>
        ) : null}
        <Wrapper>{Component}</Wrapper>
        <FootNote>{t("all-prices-are-vat-inclusive")}</FootNote>
      </Summary>
    </Container>
  );
};
